import { TimeRange } from '@delivery-metrics/shared';
import { Box, Button, Typography } from '@mui/material';
import React from 'react';

interface DateRangePickerProps {
  value: TimeRange;
  onChange: (timeRange: TimeRange) => void;
  showPresets?: boolean;
}

export type BucketMode = 'day' | 'two_day' | 'week' | 'month';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const bucketModeForRange = (start: Date, end: Date): BucketMode => {
  const startDay = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endDay = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  const totalDays = Math.max(
    1,
    Math.round((endDay - startDay) / MS_PER_DAY) + 1
  );

  if (totalDays <= 13) return 'day';
  if (totalDays <= 15) return 'two_day';
  if (totalDays <= 92) return 'week';
  return 'month';
};

export interface DroppedDays {
  mode: BucketMode;
  leadingDays: number;
  trailingDays: number;
}

const daysInMonth = (date: Date): number =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)
  ).getUTCDate();

export const droppedDaysForRange = (start: Date, end: Date): DroppedDays => {
  const mode = bucketModeForRange(start, end);
  if (mode === 'day') {
    return { mode, leadingDays: 0, trailingDays: 0 };
  }

  const startDay = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endDay = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  const totalDays = Math.max(
    1,
    Math.round((endDay - startDay) / MS_PER_DAY) + 1
  );

  if (mode === 'two_day' || mode === 'week') {
    const step = mode === 'two_day' ? 2 : 7;
    return { mode, leadingDays: 0, trailingDays: totalDays % step };
  }

  return {
    mode,
    leadingDays:
      start.getUTCDate() === 1
        ? 0
        : daysInMonth(start) - start.getUTCDate() + 1,
    trailingDays: end.getUTCDate() === daysInMonth(end) ? 0 : end.getUTCDate(),
  };
};

const pluralDays = (days: number): string =>
  `${days} day${days === 1 ? '' : 's'}`;

export const bucketNoteForRange = (start: Date, end: Date): string | null => {
  const { mode, leadingDays, trailingDays } = droppedDaysForRange(start, end);
  if (leadingDays === 0 && trailingDays === 0) {
    return null;
  }
  if (mode === 'day') {
    return null;
  }

  const unitByMode: Record<Exclude<BucketMode, 'day'>, string> = {
    two_day: '2-day',
    week: 'weekly',
    month: 'monthly',
  };
  const parts: string[] = [];
  if (leadingDays) parts.push(`the first ${pluralDays(leadingDays)}`);
  if (trailingDays) parts.push(`the last ${pluralDays(trailingDays)}`);
  const verb = leadingDays + trailingDays === 1 ? 'is' : 'are';

  return `Only complete ${unitByMode[mode]} buckets are shown: ${parts.join(' and ')} of the selected date range ${verb} not included in the charts and KPI values.`;
};

interface DatePreset {
  label: string;
  value: NonNullable<TimeRange['preset']>;
  getDates: () => { start: Date; end: Date };
}

const DATE_PRESETS: DatePreset[] = [
  {
    label: 'Last 30 Days',
    value: 'last30days',
    getDates: () => ({
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      end: new Date(),
    }),
  },
  {
    label: 'Last 90 Days',
    value: 'last90days',
    getDates: () => ({
      start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      end: new Date(),
    }),
  },
  {
    label: 'Last Month',
    value: 'lastMonth',
    getDates: () => {
      const now = new Date();
      const start = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)
      );
      const end = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59, 59, 999)
      );
      return { start, end };
    },
  },
  {
    label: 'Last 3 Months',
    value: 'last3months',
    getDates: () => {
      const now = new Date();
      const start = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 3, 1)
      );
      const end = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59, 59, 999)
      );
      return { start, end };
    },
  },
  {
    label: 'Last 6 Months',
    value: 'last6months',
    getDates: () => {
      const now = new Date();
      const start = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 6, 1)
      );
      const end = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59, 59, 999)
      );
      return { start, end };
    },
  },
  {
    label: 'Last Quarter',
    value: 'lastQuarter',
    getDates: () => {
      const now = new Date();
      const currentQuarter = Math.floor(now.getUTCMonth() / 3);
      const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const year =
        currentQuarter === 0 ? now.getUTCFullYear() - 1 : now.getUTCFullYear();
      const start = new Date(Date.UTC(year, lastQuarter * 3, 1));
      const end = new Date(
        Date.UTC(year, (lastQuarter + 1) * 3, 0, 23, 59, 59, 999)
      );
      return { start, end };
    },
  },
  {
    label: 'YTD',
    value: 'ytd',
    getDates: () => ({
      start: new Date(Date.UTC(new Date().getUTCFullYear(), 0, 1)),
      end: new Date(),
    }),
  },
  {
    label: 'Last Year',
    value: 'lastYear',
    getDates: () => {
      const now = new Date();
      const start = new Date(Date.UTC(now.getUTCFullYear() - 1, 0, 1));
      const end = new Date(
        Date.UTC(now.getUTCFullYear() - 1, 11, 31, 23, 59, 59, 999)
      );
      return { start, end };
    },
  },
];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  showPresets = true,
}) => {
  const bucketNote = bucketNoteForRange(value.start, value.end);
  const handlePresetClick = (preset: DatePreset) => {
    const dates = preset.getDates();
    onChange({
      start: dates.start,
      end: dates.end,
      preset: preset.value,
    });
  };

  return (
    <Box>
      {/* Preset Buttons */}
      {showPresets && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {DATE_PRESETS.map(preset => (
            <Button
              key={preset.value}
              size='small'
              variant={value.preset === preset.value ? 'contained' : 'outlined'}
              onClick={() => handlePresetClick(preset)}
            >
              {preset.label}
            </Button>
          ))}
        </Box>
      )}

      {/* Selected Range Display */}
      <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary' }}>
        {value.start.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        })}{' '}
        -{' '}
        {value.end.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        })}
      </Typography>
      {bucketNote && (
        <Typography
          variant='caption'
          sx={{ display: 'block', color: 'text.secondary' }}
          data-testid='date-range-bucket-note'
        >
          {bucketNote}
        </Typography>
      )}
    </Box>
  );
};
